/**
 * WhatsNewContent - Renders sanitized HTML content with DOMPurify
 */

import React, { useEffect, useRef } from "react";
import DOMPurify from "dompurify";
import PropTypes from "prop-types";

/**
 * DOMPurify configuration for What's New HTML
 */
const PURIFY_CONFIG = {
  ALLOWED_TAGS: [
    "div",
    "section",
    "article",
    "h1",
    "h2",
    "h3",
    "h4",
    "h5",
    "h6",
    "p",
    "ul",
    "ol",
    "li",
    "a",
    "strong",
    "em",
    "i",
    "span",
    "br",
    "hr",
    "img",
    "video",
    "source",
    "blockquote",
    "code",
    "pre",
  ],
  ALLOWED_ATTR: [
    "class",
    "id",
    "style",
    "href",
    "target",
    "rel",
    "src",
    "alt",
    "poster",
    "controls",
    "autoplay",
    "loop",
    "muted",
    "data-action",
    "data-id",
    "data-version",
  ],
  ALLOWED_URI_REGEXP:
    /^(https?:\/\/(cdn\.odinsight\.com|docs\.odinsight\.com|www\.odinsight\.com)|\/|#)/i,
  ALLOW_DATA_ATTR: true,
  RETURN_TRUSTED_TYPE: false,
};

/**
 * WhatsNewContent Component
 * Renders sanitized HTML with event delegation for CTAs
 */
const WhatsNewContent = ({ html, onAction }) => {
  const contentRef = useRef(null);

  useEffect(() => {
    if (!contentRef.current) return;

    /**
     * Handle clicks on CTA elements with data-action attributes
     */
    const handleClick = (e) => {
      const target = e.target.closest("[data-action]");

      if (!target) return;

      const action = target.dataset.action;
      const id = target.dataset.id;
      const version = target.dataset.version;

      e.preventDefault();

      console.log("[WhatsNewContent] CTA clicked:", { action, id, version });

      if (onAction) {
        onAction({
          action,
          id,
          version,
          element: target,
        });
      }
    };

    /**
     * Handle expandable card toggles
     */
    const handleCardToggle = (e) => {
      const cardHeader = e.target.closest(".card-header");
      if (!cardHeader) return;

      const card = cardHeader.closest(".whats-new-card");
      if (!card) return;

      console.log(
        "[WhatsNewContent] Toggling card:",
        card.classList.contains("expanded") ? "collapse" : "expand",
      );
      card.classList.toggle("expanded");
    };

    const element = contentRef.current;
    element.addEventListener("click", handleClick);
    element.addEventListener("click", handleCardToggle);

    return () => {
      element.removeEventListener("click", handleClick);
      element.removeEventListener("click", handleCardToggle);
    };
  }, [onAction]);

  // Sanitize HTML
  const sanitizedHTML = DOMPurify.sanitize(html, PURIFY_CONFIG);

  return (
    <div
      ref={contentRef}
      className="whats-new-content"
      dangerouslySetInnerHTML={{ __html: sanitizedHTML }}
    />
  );
};

WhatsNewContent.propTypes = {
  html: PropTypes.string.isRequired,
  onAction: PropTypes.func,
};

export default WhatsNewContent;
